"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@/components/ui";
import { Checkbox } from "@/components/ui/checkbox";
import { formatCurrency } from "@/lib/format-data";
interface PatientCreditPaymentOptionProps {
  balanceCents: number;
  totalCents: number;
  selected: boolean;
  disabled?: boolean;
  isPricingLoading: boolean;
  isSubmitting: boolean;
  onSelectedChange: (selected: boolean) => void;
  onPay: () => void;
}
export function PatientCreditPaymentOption(t0) {
  const $ = _c(42);
  const {
    balanceCents,
    totalCents,
    selected,
    disabled: t1,
    isPricingLoading,
    isSubmitting,
    onSelectedChange,
    onPay
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  if (isPricingLoading || balanceCents <= 0 || totalCents <= 0) {
    return null;
  }
  const appliedCreditCents = selected ? Math.min(balanceCents, totalCents) : 0;
  const cardAmountDueCents = totalCents - appliedCreditCents;
  const remainingCreditCents = balanceCents - appliedCreditCents;
  const isCreditOnly = selected && cardAmountDueCents === 0;
  const selectionDisabled = disabled || isSubmitting;
  let creditSummary;
  if (!selected) {
    creditSummary = "Your card will be charged for the full order.";
  } else {
    if (cardAmountDueCents > 0) {
      let t2;
      if ($[0] !== cardAmountDueCents) {
        t2 = formatCurrency(cardAmountDueCents);
        $[0] = cardAmountDueCents;
        $[1] = t2;
      } else {
        t2 = $[1];
      }
      creditSummary = `${t2} will be paid by card.`;
    } else {
      if (remainingCreditCents > 0) {
        let t2;
        if ($[2] !== remainingCreditCents) {
          t2 = formatCurrency(remainingCreditCents);
          $[2] = remainingCreditCents;
          $[3] = t2;
        } else {
          t2 = $[3];
        }
        creditSummary = `Your credit covers the full order. ${t2} stays on your account.`;
      } else {
        creditSummary = "Your credit covers the full order.";
      }
    }
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div><h2 className="text-h3 text-foreground">Use patient credit</h2><p className="mt-2 text-caption leading-relaxed text-foreground-secondary">Applied before your card is charged.</p></div>;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== balanceCents) {
    t3 = formatCurrency(balanceCents);
    $[5] = balanceCents;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <span className="block text-h3 text-foreground tabular-nums">{t3}</span>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="mt-1 block text-caption text-foreground-secondary">available</span>;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4) {
    t6 = <div className="mb-5 flex items-start justify-between gap-4">{t2}<p className="flex-none text-right">{t4}{t5}</p></div>;
    $[10] = t4;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== onSelectedChange) {
    t7 = checked => onSelectedChange(checked === true);
    $[12] = onSelectedChange;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== selected || $[15] !== selectionDisabled || $[16] !== t7) {
    t8 = <Checkbox checked={selected} disabled={selectionDisabled} onCheckedChange={t7} aria-label="Apply patient credit" className="mt-0.5" />;
    $[14] = selected;
    $[15] = selectionDisabled;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span>Apply patient credit</span>;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== appliedCreditCents || $[20] !== selected) {
    t10 = selected && <span className="flex-none text-status-success-text tabular-nums">{formatCurrency(appliedCreditCents)} applied</span>;
    $[19] = appliedCreditCents;
    $[20] = selected;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10) {
    t11 = <span className="flex items-center justify-between gap-4 text-body-emphasis text-foreground">{t9}{t10}</span>;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== creditSummary) {
    t12 = <span className="mt-1 block text-caption leading-relaxed text-foreground-secondary">{creditSummary}</span>;
    $[24] = creditSummary;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t11 || $[27] !== t12) {
    t13 = <span className="min-w-0 flex-1">{t11}{t12}</span>;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t13 || $[30] !== t8) {
    t14 = <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border bg-background-secondary p-4 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60">{t8}{t13}</label>;
    $[29] = t13;
    $[30] = t8;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== disabled || $[33] !== isCreditOnly || $[34] !== isSubmitting || $[35] !== onPay || $[36] !== totalCents) {
    t15 = isCreditOnly && <Button variant="primary" onClick={onPay} disabled={isSubmitting || disabled} className="mt-4 w-full cursor-pointer">{isSubmitting ? "Applying patient credit\u2026" : `Pay ${formatCurrency(totalCents)} with patient credit`}</Button>;
    $[32] = disabled;
    $[33] = isCreditOnly;
    $[34] = isSubmitting;
    $[35] = onPay;
    $[36] = totalCents;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t14 || $[39] !== t15 || $[40] !== t6) {
    t16 = <section>{t6}{t14}{t15}</section>;
    $[38] = t14;
    $[39] = t15;
    $[40] = t6;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  return t16;
}